import { yupResolver } from "@hookform/resolvers/yup";

import { TextField } from "@/components/Form/RHF/FormFields/TextField/Textfield.component";
import { useOmniForm } from "@/components/Form/RHF/Utils/useOmniForm";
import { Modal } from "@/components/General/Modal/modal";
import { APIStatus } from "@/redux/modules/api/data/wishlist/wishlist.module";
import { ModalType } from "@/redux/modules/value/modal/modal.types";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { TApiAction } from "@/redux/store.types";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";
import { OmniFormContextProvider } from "@/utils/forms/omniForm.provider";

import {
    WishlistTitleRenameFormValidationSchema,
    WishlistTitleRenameFormValues
} from "./WishlistTitleRenameModal.schema";

const FORM_ID = "wishlist-edit";

type WishlistTitleRenameModalComponentProps = {
    open: boolean;
    onRename: (newName: string) => Promise<TApiAction | undefined>;
    onCancel: () => void;
    currentName: string;
};

const WishlistTitleRenameModalComponent = ({
    open,
    onRename,
    onCancel,
    currentName
}: WishlistTitleRenameModalComponentProps) => {
    const { t } = useGranite();

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            name: currentName
        },
        resolver: yupResolver(WishlistTitleRenameFormValidationSchema)
    });

    const onSubmit = ({ name }: WishlistTitleRenameFormValues) => {
        onRename(name).then((action) => {
            if (action?.type === APIStatus.SUCCESS) {
                onCancel();
            }
        });
    };

    return (
        <Modal
            modalType={ModalType.WishlistRename}
            title={t("wishlist.title.modal.title")}
            open={open}
            autoFocus={true}
            formId={FORM_ID}
            centered={true}
            withFooter={true}
            setOpen={onCancel}
            confirmButtonText={t("wishlist.title.form.submit")}
        >
            <FormInfoProvider
                formId={FORM_ID}
                withNeverbounce={false}
            >
                {() => (
                    <OmniFormContextProvider {...methods}>
                        <form
                            id={FORM_ID}
                            onSubmit={methods.handleSubmit(onSubmit)}
                        >
                            <TextField<WishlistTitleRenameFormValues>
                                label={t("wishlist.title.form.name.label")}
                                name="name"
                                required={true}
                            />
                        </form>
                    </OmniFormContextProvider>
                )}
            </FormInfoProvider>
        </Modal>
    );
};

export { WishlistTitleRenameModalComponent };
